import React, { useState } from 'react';
import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Table,
    Badge
} from 'reactstrap';
import { Eye, Edit, Trash2, ChevronDown, Lock, UserCheck, UserX, Mail, Calendar, Clock } from 'react-feather';
import { format, isValid } from 'date-fns';

interface UserActionDropdownProps {
    user: any;
    onEditClick: (user: any) => void;
    onPasswordClick: (user: any) => void;
    onToggleStatus: (user: any) => void;
    onDeleteClick: (id: number) => void;
}

const UserActionDropdown: React.FC<UserActionDropdownProps> = ({
    user,
    onEditClick,
    onPasswordClick,
    onToggleStatus,
    onDeleteClick
}) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);

    // Format date for display
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '---';
        
        try {
            const date = new Date(dateString);
            if (!isValid(date)) return '---';
            return format(date, 'dd MMM yyyy, hh:mm a');
        } catch {
            return '---';
        }
    };

    // Get role badge color
    const getRoleBadge = (role: boolean) => {
        return role ? 'primary' : 'secondary';
    };

    // Get status badge color
    const getStatusBadge = (isActive: boolean) => {
        return isActive ? 'success' : 'danger';
    };

    return (
        <>
            <Dropdown
                isOpen={dropdownOpen}
                toggle={() => setDropdownOpen(!dropdownOpen)}
            >
                <DropdownToggle
                    tag="button"
                    color="primary"
                    size="sm"
                    className="d-flex align-items-center gap-1"
                    style={{ minWidth: '100px' }}
                >
                    Actions <ChevronDown size={14} />
                </DropdownToggle>
                <DropdownMenu>
                    {/* View Details */}
                    <DropdownItem
                        onClick={() => setViewModalOpen(true)}
                        className="d-flex align-items-center"
                    >
                        <Eye size={16} className="me-2" /> View Details
                    </DropdownItem>

                    {/* Edit User */}
                    <DropdownItem
                        onClick={() => onEditClick(user)}
                        className="d-flex align-items-center"
                    >
                        <Edit size={16} className="me-2" /> Edit User
                    </DropdownItem>

                    {/* Change Password */}
                    <DropdownItem
                        onClick={() => onPasswordClick(user)}
                        className="d-flex align-items-center"
                    >
                        <Lock size={16} className="me-2" /> Change Password
                    </DropdownItem>

                    <DropdownItem divider />

                    {/* Toggle Status */}
                    <DropdownItem
                        onClick={() => onToggleStatus(user)}
                        className={`d-flex align-items-center ${user.isActive ? 'text-warning' : 'text-success'}`}
                    >
                        {user.isActive ? (
                            <>
                                <UserX size={16} className="me-2" /> Deactivate User
                            </>
                        ) : (
                            <>
                                <UserCheck size={16} className="me-2" /> Activate User
                            </>
                        )}
                    </DropdownItem>

                    {/* Delete User */}
                    <DropdownItem
                        onClick={() => onDeleteClick(user.id)}
                        className="text-danger d-flex align-items-center"
                    >
                        <Trash2 size={16} className="me-2" /> Delete User
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>

            {/* View Details Modal */}
            <Modal
                isOpen={viewModalOpen}
                toggle={() => setViewModalOpen(!viewModalOpen)}
                size="lg"
                centered
            >
                <ModalHeader toggle={() => setViewModalOpen(false)}>
                    <div className="d-flex align-items-center">
                        <div>
                            User Details
                            <small className="text-muted ms-2">#{user.id}</small>
                        </div>
                        <div className="ms-3">
                            <Badge color={getRoleBadge(user.role)} className="me-2">
                                {user.role ? 'Admin' : 'User'}
                            </Badge>
                            <Badge color={getStatusBadge(user.isActive)}>
                                {user.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                        </div>
                    </div>
                </ModalHeader>

                <ModalBody>
                    <div className="row">
                        {/* User Information */}
                        <div className="col-md-6">
                            <h6 className="border-bottom pb-2 mb-3">
                                <UserCheck size={16} className="me-2" />
                                User Information
                            </h6>

                            <Table borderless size="sm">
                                <tbody>
                                    <tr>
                                        <td className="fw-semibold" style={{ width: '40%' }}>
                                            <Mail size={14} className="me-1" /> Username:
                                        </td>
                                        <td>{user.username || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">
                                            <Mail size={14} className="me-1" /> Email:
                                        </td>
                                        <td>{user.email || 'N/A'}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">User ID:</td>
                                        <td>#{user.id}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>

                        {/* Dates & Sessions */}
                        <div className="col-md-6">
                            <h6 className="border-bottom pb-2 mb-3">
                                <Clock size={16} className="me-2" />
                                Activity Information
                            </h6>

                            <Table borderless size="sm">
                                <tbody>
                                    <tr>
                                        <td className="fw-semibold">
                                            <Calendar size={14} className="me-1" /> Created:
                                        </td>
                                        <td>{formatDate(user.createdAt)}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">
                                            <Clock size={14} className="me-1" /> Last Login:
                                        </td>
                                        <td>{formatDate(user.lastLogin)}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">
                                            <Clock size={14} className="me-1" /> Last Updated:
                                        </td>
                                        <td>{formatDate(user.updatedAt)}</td>
                                    </tr>
                                    <tr>
                                        <td className="fw-semibold">Active Sessions:</td>
                                        <td>
                                            <Badge color="info" pill>
                                                {user._count?.refreshTokens || 0}
                                            </Badge>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </div>

                    {/* Sessions Information */}
                    {user.refreshTokens && user.refreshTokens.length > 0 && (
                        <div className="mt-4">
                            <h6 className="border-bottom pb-2 mb-3">Recent Sessions</h6>
                            <Table size="sm" bordered>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Created</th>
                                        <th>Expires</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {user.refreshTokens.slice(0, 3).map((token: any, index: number) => (
                                        <tr key={token.id}>
                                            <td>{index + 1}</td>
                                            <td>{formatDate(token.createdAt)}</td>
                                            <td>{formatDate(token.expiresAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            {user._count?.refreshTokens > 3 && (
                                <small className="text-muted">
                                    And {user._count.refreshTokens - 3} more sessions...
                                </small>
                            )}
                        </div>
                    )}
                </ModalBody>

                <ModalFooter className="justify-content-between">
                    <div>
                        <small className="text-muted">
                            User ID: <strong>#{user.id}</strong>
                        </small>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            color="light"
                            onClick={() => setViewModalOpen(false)}
                        >
                            Close
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                setViewModalOpen(false);
                                onEditClick(user);
                            }}
                        >
                            <Edit size={16} className="me-1" /> Edit User
                        </Button>
                        <Button
                            color="warning"
                            onClick={() => {
                                setViewModalOpen(false);
                                onPasswordClick(user);
                            }}
                        >
                            <Lock size={16} className="me-1" /> Change Password
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default UserActionDropdown;